<script setup>
import {
  LCircleMarker,
  LControl,
  LIcon,
  LMap,
  LMarker,
  LTileLayer
} from "@vue-leaflet/vue-leaflet";
import "leaflet/dist/leaflet.css";
import { ref, watch, onMounted, onBeforeMount } from "vue";
import MapKey from "../map/MapKey.vue";
import MapZoom from "../map/MapZoom.vue";
import LRoutingMachine from "../map/LeafletRoutingMachine.vue";
const props = defineProps({
  tilesUrl: {
    type: String,
    default: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
  },
  markerList: {
    type: Array,
    required: true
  },
  defaultSelectedMarker: {
    type: Object,
    default: void 0
  },
  /** If this isn't set, we use Geolocalisation to center the map or fallback on Strasbourg */
  mapCenter: {
    type: Object,
    default: void 0
  },
  mapZoom: {
    type: Number,
    default: 9
  },
  centerOnClickedMarker: {
    type: Boolean,
    default: true
  },
  drawCircleAroundMarker: {
    type: Boolean,
    default: true
  },
  hideControls: {
    type: Boolean,
    default: false
  },
  minZoom: {
    type: Number,
    default: 0
  },
  maxZoom: {
    type: Number,
    default: 18
  },
  keyGroups: {
    type: Array,
    default: () => []
  },
  keyTitle: {
    type: String,
    default: ""
  },
  externalBounds: {
    type: Object,
    default: void 0
  },
  routingMachineProps: {
    type: Object,
    default: void 0
  },
  hasB2CStyles: {
    type: Boolean,
    default: false
  }
});
const emit = defineEmits([
  "markerClicked",
  "centerUpdated",
  "boundsUpdated",
  "zoomUpdated",
  "routeBoxesFound",
  "centeredOnUser"
]);
const mapRef = ref(void 0);
const zoom = ref(1);
const center = ref([48.5734053, 7.7521113]);
const bounds = ref(void 0);
const circleMarker = ref({
  center: void 0,
  radius: 28,
  color: "#B1C3D4"
});
const selectedMarker = ref(void 0);
const localizationCookie = ref(void 0);
const memoizedBounds = ref(void 0);
const isMapKeyOpen = ref(false);
const loading = ref(true);
const markerIsCluster = (marker) => marker.type === "cluster";
const getCluster = (marker) => marker;
const centerUpdated = (centerCoordinates) => {
  emit("centerUpdated", centerCoordinates);
};
const boundsUpdated = (boundsCoordinates) => {
  if (JSON.stringify(boundsCoordinates) !== JSON.stringify(memoizedBounds.value)) {
    memoizedBounds.value = boundsCoordinates;
    emit(
      "boundsUpdated",
      boundsCoordinates,
      // We inverse the bounds for the API needs
      inverseBounds(boundsCoordinates)
    );
  }
};
const zoomUpdated = (zoomValue) => {
  emit("zoomUpdated", zoomValue);
};
const markerClicked = (marker, event) => {
  const target = event?.originalEvent?.target;
  if (event && !target?.closest(".leaflet-marker-icon")) {
    return;
  }
  selectedMarker.value = marker;
  emit("markerClicked", marker);
};
const getPositionSuccess = (position) => {
  setTimeout(() => {
    center.value = [position.coords.latitude, position.coords.longitude];
    emit("centeredOnUser", center.value);
  }, 250);
  if (!localizationCookie.value) {
    sessionStorage.setItem(
      "geolocalisation",
      [position.coords.latitude, position.coords.longitude].toString()
    );
    localizationCookie.value = [
      position.coords.latitude,
      position.coords.longitude
    ];
  }
};
const getPositionFailed = () => {
  if (localizationCookie.value) {
    setTimeout(() => {
      center.value = localizationCookie.value;
    }, 50);
  }
};
const zoomIn = () => {
  if (props.maxZoom > zoom.value) {
    zoom.value += 1;
  }
};
const zoomOut = () => {
  if (props.minZoom < zoom.value) {
    zoom.value -= 1;
  }
};
const inverseBounds = (bounds2) => ({
  NW: {
    lat: bounds2._northEast.lat,
    lng: bounds2._southWest.lng
  },
  SE: {
    lat: bounds2._southWest.lat,
    lng: bounds2._northEast.lng
  }
});
const onSelectedMarkerChange = (newMarker) => {
  if (props.centerOnClickedMarker && newMarker) {
    center.value = newMarker.latLng;
    if (markerIsCluster(newMarker) && zoom.value < props.maxZoom) {
      setTimeout(() => {
        zoom.value += 1;
      }, 250);
    }
    if (props.drawCircleAroundMarker && !markerIsCluster(newMarker)) {
      circleMarker.value.center = newMarker.latLng;
    }
  }
};
const centerOnUser = () => {
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
      getPositionSuccess,
      getPositionFailed
    );
  }
};
onBeforeMount(() => {
  const geolocalisation = sessionStorage.getItem("geolocalisation");
  if (geolocalisation) {
    localizationCookie.value = geolocalisation.split(",").map((value) => parseFloat(value));
  }
  if (props.mapCenter && !props.defaultSelectedMarker) {
    center.value = props.mapCenter;
  } else if (props.centerOnClickedMarker && !props.defaultSelectedMarker) {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        getPositionSuccess,
        getPositionFailed
      );
    }
  }
  if (props.centerOnClickedMarker && props.defaultSelectedMarker) {
    selectedMarker.value = props.defaultSelectedMarker;
    center.value = props.defaultSelectedMarker.latLng;
    circleMarker.value.center = props.defaultSelectedMarker.latLng;
  }
});
onMounted(() => {
  setTimeout(() => {
    zoom.value = props.mapZoom;
  }, 200);
});
watch(selectedMarker, (newMarker) => {
  onSelectedMarkerChange(newMarker);
});
watch(
  () => props.markerList,
  (newList) => {
    if (loading.value && newList.length > 0) {
      selectedMarker.value = props.defaultSelectedMarker;
      loading.value = false;
    }
  }
);
watch(
  () => props.externalBounds,
  (newBounds) => {
    if (newBounds) {
      mapRef.value?.leafletObject?.fitBounds([newBounds], {
        maxZoom: 14
      });
    }
  }
);
defineExpose({
  centerOnUser
});
</script>

<template>
    <div
        class="w-full h-full relative"
        :class="isMapKeyOpen && 'overflow-hidden'"
    >
        <ClientOnly>
            <LMap
                ref="mapRef"
                v-model:zoom="zoom"
                v-model:bounds="bounds"
                v-model:center="center"
                :min-zoom
                :max-zoom
                use-global-leaflet
                class="z-10"
                :options="{ zoomControl: false }"
                @update:center="centerUpdated"
                @update:bounds="boundsUpdated"
                @update:zoom="zoomUpdated"
                @click="isMapKeyOpen = false"
            >
                <LRoutingMachine
                    v-if="routingMachineProps?.routerToken"
                    v-bind="routingMachineProps"
                    @routeBoxes="
  ({ NW, SE }) => {
    emit('routeBoxesFound', { NW, SE });
  }
"
                />
                <LTileLayer
                    :url="tilesUrl"
                    attribution="<a href='http://jawg.io' title='Tiles Courtesy of Jawg Maps' target='_blank'>&copy; <b>Jawg</b>Maps</a> &copy; <a href='https://www.openstreetmap.org/copyright'>OpenStreetMap</a> contributors"
                />
                <LControl v-if="!hideControls" position="bottomright">
                    <button
                        type="button"
                        class="size-11 m-1.5 bg-white p-1 shadow border rounded-full"
                        :class="
  hasB2CStyles ? 'border-gray-200 hover:bg-zinc-100' : 'border-opacity-20 border-black hover:bg-gray-100'
"
                        @click="centerOnUser"
                    >
                        <IconPosition
                            class="m-auto"
                            :class="
  hasB2CStyles ? 'size-6 text-zinc-800' : 'size-5 text-slate-500'
"
                        />
                    </button>
                    <MapZoom
                        @zoom-in="zoomIn"
                        @zoom-out="zoomOut"
                        :has-b-2-c-styles
                    />
                    <MapKey
                        v-if="keyGroups.length"
                        v-model="isMapKeyOpen"
                        :key-groups
                        :title="keyTitle"
                    />
                </LControl>
                <LCircleMarker
                    v-if="circleMarker.center"
                    :lat-lng="circleMarker.center"
                    :radius="circleMarker.radius"
                    :fill-color="circleMarker.color"
                    :fill-opacity="0.8"
                    :stroke="false"
                />
                <LMarker
                    v-for="marker in markerList"
                    v-show="markerList"
                    :key="marker.id"
                    :lat-lng="marker.latLng"
                    @click="(e) => markerClicked(marker, e)"
                >
                    <LIcon
                        v-if="!markerIsCluster(marker)"
                        :icon-url="marker.icon"
                        :icon-size="[40, 40]"
                    />
                    <LIcon v-else>
                        <div class="size-8">
                            <NuxtImg
                                :src="marker.icon"
                                class="-m-4 size-8"
                                alt="Cluster"
                            />
                            <div class="absolute flex -m-4 size-8">
                                <span class="m-auto text-white text-base">
                                    {{ getCluster(marker).count }}
                                </span>
                            </div>
                        </div>
                    </LIcon>
                </LMarker>
            </LMap>
        </ClientOnly>
    </div>
</template>
